import { useEffect, useMemo, useState } from 'react';
import SideBar from '../Sidebar/SideBar';
import { Icon } from '@iconify/react/dist/iconify.js';
import Loader from '../Loader';
import ClientSubscriptionApi from '../../Api/ClientSubscription';
import Branches from '../../Api/Branches';
import Package from '../../Api/Package';
import DbBackupApi from '../../Api/DbBackup';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Swal from 'sweetalert2';

const EXPIRY_WINDOW_DAYS = 45;
const PAGE_LIMIT = 100;

const Dashboard = () => {
    const [loading, setLoading] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState([]);
    const [branchData, setBranchData] = useState([]);
    const [packageData, setPackageData] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [dashboardClientData, setDashboardClientData] = useState([]);
    const [showDbBackupBtn, setShowDbBackupBtn] = useState(false);
    const [backupLoading, setBackupLoading] = useState(false);

    const parseDate = (value) => {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const formatDate = (value) => {
        const date = parseDate(value);
        if (!date) return '-';
        return date.toLocaleDateString('en-GB');
    };

    const fetchDashboardData = async (currentPage = 1) => {
        setLoading(true);
        try {
            const [subscriptionsResponse, branchesResponse] = await Promise.all([
                ClientSubscriptionApi.GetAllClientsSub(),
                Branches.GetAllClients(currentPage, PAGE_LIMIT),
            ]);
            setSubscriptionData(subscriptionsResponse?.Result || []);
            setBranchData(branchesResponse?.Result || []);
            setTotalPages(branchesResponse?.TotalPages || 1);
        } catch (error) {
            console.error('Dashboard load failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboardOnlyData = async () => {
        try {
            const response = await Branches.GetAllClients(1, 1000);
            setDashboardClientData(response?.Result || []);
        } catch (error) {
            console.error('Dashboard data load failed:', error);
        }
    };

    useEffect(() => {
        fetchDashboardOnlyData();
    }, []);

    const fetchPackageData = async () => {
        try {
            const response = await Package.GetAllPackage();
            setPackageData(response?.Result || []);
        } catch (error) {
            console.error('Package load failed:', error);
        }
    };

    useEffect(() => {
        fetchDashboardData(pageNo);
    }, [pageNo]);

    useEffect(() => {
        fetchPackageData();
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.altKey && (e.key === 'n' || e.key === 'N')) {
                e.preventDefault();
                setShowDbBackupBtn(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const handleDbBackupDownload = async () => {
        setBackupLoading(true);
        try {
            const resp = await DbBackupApi.DownloadBackup();
            const contentType = resp.headers['content-type'] || '';
            if (contentType.includes('application/json')) {
                const text = await resp.data.text();
                const parsed = JSON.parse(text);
                throw new Error(parsed?.ErrorMessages || 'DB backup failed');
            }
            const disposition = resp.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/i);
            const fileName = match?.[1] || `db_backup_${Date.now()}.zip`;
            const blob = new Blob([resp.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            Swal.fire({
                title: 'Success',
                text: 'DB backup downloaded successfully',
                icon: 'success',
                width: '320px',
            });
        } catch (err) {
            let message = 'DB backup failed';
            if (err?.response?.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const parsed = JSON.parse(text);
                    message = parsed?.ErrorMessages || message;
                } catch (_) {
                    /* keep default */
                }
            } else if (err?.message) {
                message = err.message;
            }
            Swal.fire({ title: 'Error', text: message, icon: 'error', width: '360px' });
        } finally {
            setBackupLoading(false);
        }
    };

    const dashboardMetrics = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const oneDayMs = 1000 * 60 * 60 * 24;

        const normalizeId = (value) => {
            if (value === undefined || value === null) return null;
            return String(value);
        };

        const branchById = dashboardClientData.reduce((acc, branch) => {
            const id = normalizeId(branch.id);
            if (id) acc[id] = branch;
            return acc;
        }, {});

        const packageById = packageData.reduce((acc, pkg) => {
            const id = normalizeId(pkg.id);
            if (id) {
                acc[id] = pkg;
            }
            return acc;
        }, {});

        const getRecordBranchId = (record) => normalizeId(record.BranchId ?? record.branchId ?? record.id);
        const hasSubscriptionData = subscriptionData.length > 0;
        const subscriptionBranchIds = new Set(
            subscriptionData.map(getRecordBranchId).filter(Boolean)
        );
        const branchOnlyRecords = dashboardClientData.filter(
            (branch) => {
                const branchId = normalizeId(branch.id);
                return branchId && !subscriptionBranchIds.has(branchId);
            }
        );

        const sourceRecords = hasSubscriptionData
            ? [...subscriptionData, ...branchOnlyRecords]
            : dashboardClientData;

        const mappedRecords = sourceRecords.map((item) => {
            const rawBranchId = item.BranchId ?? item.branchId ?? item.id;
            const branchId = normalizeId(rawBranchId);
            const relatedBranch = branchById[branchId];
            const expiryRaw =
                item.Exp_Dt ||
                item.expiryDate ||
                item.ServiceExpiryDate ||
                item.ExpiryDate ||
                relatedBranch?.ServiceExpiryDate ||
                relatedBranch?.ExpiryDate;
            const statusRaw =
                item.Status ??
                item.status ??
                (relatedBranch?.StopWorking ? 'inactive' : 'use');

            const rawPackageId =
                item.packageId ??
                item.PackageId ??
                relatedBranch?.packageId ??
                relatedBranch?.PackageId;
            const normalizedPackageId = normalizeId(rawPackageId);
            const packageNameFromRecord = item.packageName ?? item.PackageName;
            const packageNameFromBranch = relatedBranch?.packageName ?? relatedBranch?.PackageName;
            const resolvedPackageName =
                packageNameFromRecord ||
                packageNameFromBranch ||
                (normalizedPackageId ? packageById[normalizedPackageId]?.name : null) ||
                (rawPackageId ? String(rawPackageId) : null) ||
                '-';

            const expiryDate = parseDate(expiryRaw);
            const daysLeft = expiryDate ? Math.ceil((expiryDate - today) / oneDayMs) : null;
            const normalizedStatus = (statusRaw || '').toString().toLowerCase();
            const isExpired = daysLeft !== null && daysLeft < 0;
            const isExpiringSoon =
                daysLeft !== null && daysLeft >= 0 && daysLeft <= EXPIRY_WINDOW_DAYS;
            const isUsingSoftware =
                !isExpired &&
                (normalizedStatus === 'use' ||
                    normalizedStatus === 'active' ||
                    normalizedStatus === '');

            return {
                ...item,
                BranchId: branchId ?? rawBranchId,
                Exp_Dt: expiryRaw,
                Status: statusRaw,
                daysLeft,
                isExpired,
                isExpiringSoon,
                isUsingSoftware,
                packageLabel: resolvedPackageName,
            };
        });

        const usingRows = mappedRecords
            .filter((item) => item.isUsingSoftware)
            .sort((a, b) => (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999));
        const nearExpiryRows = mappedRecords
            .filter((item) => item.isExpiringSoon)
            .sort((a, b) => (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999));
        const expiredRows = mappedRecords
            .filter((item) => item.isExpired)
            .sort((a, b) => (b.daysLeft ?? -9999) - (a.daysLeft ?? -9999));

        return {
            totalClients: dashboardClientData.length,
            usingSoftwareCount: usingRows.length,
            nearExpiryCount: nearExpiryRows.length,
            expiredCount: expiredRows.length,
            usingRows,
            nearExpiryRows,
            expiredRows,
        };
    }, [subscriptionData, dashboardClientData, packageData]);

    const graphData = [
        { name: 'Using Software', value: dashboardMetrics.usingSoftwareCount, color: 'using' },
        { name: 'Near Expiry', value: dashboardMetrics.nearExpiryCount, color: 'near-expiry' },
        { name: 'Expired', value: dashboardMetrics.expiredCount, color: 'expired' },
    ];

    const maxValue = Math.max(dashboardMetrics.totalClients || 1, 1);

    const getBranchName = (branchId) => {
        const branch = dashboardClientData.find((item) => String(item.id) === String(branchId));
        return branch?.branchName || branchId || '-';
    };

    const COLORS = ['#dda036', // amber - warning / near expiry
  '#df2a2a', // red - expired / critical
  '#4617b4',];

    return (
        <div className='Flex-general-client-sidebar'>
            <div className='sidebar-gen-width'>
                <SideBar />
            </div>
            <div className='Main-general-Client'>
                <div className='header-general' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className='general-card-title'>Dashboard</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {showDbBackupBtn && (
                            <button
                                style={{ backgroundColor: '#0f766e', color: 'white' }}
                                className='extend-validity'
                                onClick={handleDbBackupDownload}
                                disabled={backupLoading}
                            >
                                {backupLoading ? 'Backing up...' : 'DB Backup'}
                            </button>
                        )}
                        <button style={{ backgroundColor: "white", color: "black" }}
                            className='extend-validity'
                            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
                            disabled={pageNo === 1}
                        >
                            Prev
                        </button>
                        <span style={{ color: "white" }}>Page {pageNo} of {totalPages}</span>
                        <button style={{ backgroundColor: "white", color: "black" }}
                            className='extend-validity'
                            onClick={() => setPageNo((prev) => (prev < totalPages ? prev + 1 : prev))}
                            disabled={pageNo >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div className='dashboard-section' >
                    <div className="dashboard-overview-row" style={{ display: 'flex', gap: '20px', alignItems: 'stretch' }}>
                        <div className="dashboard-metric-col" style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '300px' }}>

                            <div className='dashboard-card'>
                                <Icon icon='heroicons:user-group-20-solid' style={{ fontSize: "26px" }} />
                                <div style={{ width: "70%" }}>
                                    <p style={{ textAlign: "center" }}>Total Clients</p>
                                    <h3 style={{ textAlign: "center" }}>{dashboardMetrics.totalClients}</h3>
                                </div>
                            </div>

                            <div className='dashboard-card'>
                                <Icon icon='mdi:application-cog' style={{ fontSize: "26px" }} />
                                <div style={{ width: "70%" }}>
                                    <p style={{ textAlign: "center" }}>Using Software</p>
                                    <h3 style={{ textAlign: "center" }}>{dashboardMetrics.usingSoftwareCount}</h3>
                                </div>
                            </div>

                            <div className='dashboard-card'>
                                <Icon icon='mdi:clock-alert' style={{ fontSize: "26px" }} />
                                <div style={{ width: "70%" }}>
                                    <p style={{ textAlign: "center" }}>Expiry In {EXPIRY_WINDOW_DAYS} Days</p>
                                    <h3 style={{ textAlign: "center" }}>{dashboardMetrics.nearExpiryCount}</h3>
                                </div>
                            </div>

                        </div>

                        {/* RIGHT: PIE CHART */}
                        <div style={{ flex: 1, background: '#fff', borderRadius: '10px', padding: '2px' }}>
                            {/* <div className='company_info-text-im'>Subscription Overview</div> */}

                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={graphData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={110}
                                        paddingAngle={6}
                                        label
                                    >
                                        {graphData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={COLORS[index % COLORS.length]}
                                            />
                                        ))}
                                    </Pie>

                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            borderRadius: '10px',
                                            border: 'none',
                                            color: '#fff',
                                            fontWeight: "600",
                                            border: "1px solid black"
                                        }}
                                    />

                                    <Legend
                                        formatter={(value, entry) => (
                                            <span style={{ fontWeight: 600, fontSize: 14, }}>
                                                {entry.payload.name}
                                            </span>
                                        )}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className='company_info-text-im'>Near Expiry Clients (Table)</div>
                    <div className='table-account-manager dashboard-table-wrap'>
                        <table className='table-master-acc'>
                            <thead className='header-table-account'>
                                <tr>
                                    <th className='table-header-manager ac-sno-wth'>S.No</th>
                                    <th className='table-header-manager'>Branch</th>
                                    <th className='table-header-manager'>Package</th>
                                    <th className='table-header-manager'>Status</th>
                                    <th className='table-header-manager'>Expiry Date</th>
                                    <th className='table-header-manager'>Days Left</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardMetrics.nearExpiryRows.length > 0 ? (
                                    dashboardMetrics.nearExpiryRows.map((item, idx) => (
                                        <tr key={`near-${item.id || `${item.BranchId}-${idx}`}`}>
                                            <td className='table-data-account ac-sno-wth'>{idx + 1}</td>
                                            <td className='table-data-account'>{getBranchName(item.BranchId)}</td>
                                            <td className='table-data-account'>{item.packageLabel || '-'}</td>
                                            <td className='table-data-account'>{item.Status || '-'}</td>
                                            <td className='table-data-account'>{formatDate(item.Exp_Dt)}</td>
                                            <td className='table-data-account'>{item.daysLeft}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className='table-data-account' colSpan={6}>
                                            No clients are near expiry in the next {EXPIRY_WINDOW_DAYS} days.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className='company_info-text-im'>
                        Already Using Clients (Table) - Total: {dashboardMetrics.usingRows.length}
                    </div>
                    <div className='table-account-manager dashboard-table-wrap'>
                        <table className='table-master-acc'>
                            <thead className='header-table-account'>
                                <tr>
                                    <th className='table-header-manager ac-sno-wth'>S.No</th>
                                    <th className='table-header-manager'>Branch</th>
                                    <th className='table-header-manager'>Package</th>
                                    <th className='table-header-manager'>Status</th>
                                    <th className='table-header-manager'>Expiry Date</th>
                                    <th className='table-header-manager'>Days Left</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardMetrics.usingRows.length > 0 ? (
                                    dashboardMetrics.usingRows.map((item, idx) => (
                                        <tr key={`using-${item.id || `${item.BranchId}-${idx}`}`}>
                                            <td className='table-data-account ac-sno-wth'>{idx + 1}</td>
                                            <td className='table-data-account'>{getBranchName(item.BranchId)}</td>
                                            <td className='table-data-account'>{item.packageLabel || '-'}</td>
                                            <td className='table-data-account'>{item.Status || '-'}</td>
                                            <td className='table-data-account'>{formatDate(item.Exp_Dt)}</td>
                                            <td className='table-data-account'>{item.daysLeft ?? '-'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className='table-data-account' colSpan={6}>
                                            No clients are currently using the software.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className='company_info-text-im'>Already Expired Clients (Table)</div>
                    <div className='table-account-manager dashboard-table-wrap'>
                        <table className='table-master-acc'>
                            <thead className='header-table-account'>
                                <tr>
                                    <th className='table-header-manager ac-sno-wth'>S.No</th>
                                    <th className='table-header-manager'>Branch</th>
                                    <th className='table-header-manager'>Package</th>
                                    <th className='table-header-manager'>Status</th>
                                    <th className='table-header-manager'>Expiry Date</th>
                                    <th className='table-header-manager'>Expired Days</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardMetrics.expiredRows.length > 0 ? (
                                    dashboardMetrics.expiredRows.map((item, idx) => (
                                        <tr key={`expired-${item.id || `${item.BranchId}-${idx}`}`}>
                                            <td className='table-data-account ac-sno-wth'>{idx + 1}</td>
                                            <td className='table-data-account'>{getBranchName(item.BranchId)}</td>
                                            <td className='table-data-account'>{item.packageLabel || '-'}</td>
                                            <td className='table-data-account'>{item.Status || '-'}</td>
                                            <td className='table-data-account'>{formatDate(item.Exp_Dt)}</td>
                                            <td className='table-data-account'>{Math.abs(item.daysLeft ?? 0)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className='table-data-account' colSpan={6}>
                                            No clients are already expired.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {loading && <Loader />}
        </div >
    );
};

export default Dashboard;
